/**
 * Board / meal-plan basis resolution for persisted hotel orders (getOrders payload, success page, PDF).
 * Sources: primarily `rooms[].roombasis` / Gimmonix-style synonyms; fallback to parsed `order_response` or package fields.
 */

export const HOTEL_BOARD_BASIS_NOT_SPECIFIED_HE = "לא צוין";

/** Hebrew labels per product requirement (confirmation + dashboard). */
const BOARD_CODE_HE = Object.freeze({
  BB: "לינה וארוחת בוקר",
  HB: "חצי פנסיון",
  FB: "פנסיון מלא",
  AI: "הכל כלול",
  RO: "לינה בלבד",
});

const KNOWN_CODES = new Set(Object.keys(BOARD_CODE_HE));

function parseOrderResponseObject(order) {
  if (!order || typeof order !== "object") return null;
  let r = order.order_response;
  if (typeof r === "string" && r.trim().length > 2) {
    try {
      const p = JSON.parse(r);
      r = typeof p === "object" && p ? p : null;
    } catch {
      return null;
    }
  }
  if (!r || typeof r !== "object" || Array.isArray(r)) return null;
  return r;
}

export function pickFirstNonEmptyTrimmed(values) {
  for (const v of values) {
    if (v == null) continue;
    const t = String(v).trim();
    if (t) return t;
  }
  return null;
}

function pickBoardRawFromObject(obj) {
  if (!obj || typeof obj !== "object") return null;
  return pickFirstNonEmptyTrimmed([
    obj.roombasis,
    obj.roomBasis,
    obj.RoomBasis,
    obj.boardBasis,
    obj.board_basis,
    obj.board,
    obj.Board,
    obj.boardCode,
    obj.boardName,
    obj.BoardName,
    obj.mealPlan,
    obj.MealPlan,
    obj.mealPlanCode,
    obj.mealPlanName,
    obj.ratePlan,
    obj.rate_plan,
    obj.basis,
    obj.accommodationBasis,
    obj.AccommodationBasis,
  ]);
}

function findReservationRoomMirror(or, room) {
  if (!or || !room || typeof or !== "object") return null;
  const rid = room.roomid ?? room.roomId ?? room.RoomId;
  const lists = [];

  const res = or.reservation;
  if (res && typeof res === "object") {
    lists.push(res.hotel?.rooms, res.hotelRooms, res.rooms, res.roomStays);
  }
  lists.push(or.hotel?.rooms, or.rooms);

  for (const list of lists) {
    if (!Array.isArray(list)) continue;
    for (const item of list) {
      if (!item || typeof item !== "object") continue;
      const itemRid = item.roomid ?? item.roomId ?? item.RoomId ?? item.id;
      if (rid != null && itemRid != null && String(itemRid) === String(rid)) {
        return item;
      }
    }
  }
  return null;
}

/**
 * Best-effort raw board value from supplier / DB (before Hebrew formatting).
 *
 * @param {Record<string, unknown>|null|undefined} room
 * @param {Record<string, unknown>|null|undefined} order
 * @returns {string|null}
 */
export function resolveHotelRoomBoardBasis(room, order) {
  const direct = pickBoardRawFromObject(room);
  if (direct) return direct;

  const or = parseOrderResponseObject(order);
  const mirror = findReservationRoomMirror(or, room);
  const fromMirror = pickBoardRawFromObject(mirror);
  if (fromMirror) return fromMirror;

  if (order && typeof order === "object") {
    const fromOrderRoot = pickFirstNonEmptyTrimmed([
      order.roombasis,
      order.roomBasis,
      order.board,
      order.boardBasis,
      order.mealPlan,
    ]);
    if (fromOrderRoot) return fromOrderRoot;

    try {
      const pdRaw = order.packagepricedetails;
      const pd =
        typeof pdRaw === "string" && pdRaw.trim().length > 2 ? JSON.parse(pdRaw) : pdRaw;
      const fromPd = pickBoardRawFromObject(pd && typeof pd === "object" ? pd : null);
      if (fromPd) return fromPd;
    } catch {
      /* ignore malformed packagepricedetails JSON */
    }
  }

  return null;
}

/**
 * Extract canonical board code BB/HB/... from messy strings ("Bed and Breakfast BB").
 *
 * @param {unknown} raw
 * @returns {string} code or ""
 */
export function extractBoardBasisCode(raw) {
  const text = String(raw ?? "").trim();
  if (!text) return "";
  const tokens = text
    .toUpperCase()
    .split(/\s+/u)
    .filter(Boolean);

  if (tokens.length === 1 && KNOWN_CODES.has(tokens[0])) return tokens[0];

  const last = tokens[tokens.length - 1];
  if (KNOWN_CODES.has(last)) return last;

  const first = tokens[0];
  if (KNOWN_CODES.has(first)) return first;

  return "";
}

/**
 * Prefer Hebrew mapped label for known codes; otherwise return a cleaned raw string.
 * Null/blank → לא צוין.
 *
 * @param {unknown} value
 * @returns {string}
 */
export function formatBoardBasisLabel(value) {
  if (value == null) return HOTEL_BOARD_BASIS_NOT_SPECIFIED_HE;

  const text = String(value).trim();
  if (!text) return HOTEL_BOARD_BASIS_NOT_SPECIFIED_HE;

  const code = extractBoardBasisCode(text);
  if (code && BOARD_CODE_HE[code]) return BOARD_CODE_HE[code];

  return text.replace(/\s+/gu, " ");
}

function aggregateRoomSources(order, rooms) {
  const list = Array.isArray(rooms) ? rooms : [];
  const resolved = [];
  const missingRooms = [];

  /** @type {Array<{roomIndex:number, fields: Record<string,string>}>} */
  const rawBoardFields = [];

  list.forEach((room, idx) => {
    const fields = {};
    if (room && typeof room === "object") {
      for (const k of Object.keys(room)) {
        if (!/board|basis|meal|plan|rate|pension/i.test(k)) continue;
        const v = room[k];
        if (v == null) continue;
        const s = String(v).trim();
        if (!s) continue;
        fields[k] = s.length > 160 ? `${s.slice(0, 160)}…` : s;
      }
    }

    rawBoardFields.push({
      roomIndex: idx + 1,
      fields,
    });

    const raw = resolveHotelRoomBoardBasis(room, order);
    const label = formatBoardBasisLabel(raw);
    resolved.push(label);
    if (!raw || !String(raw).trim()) missingRooms.push(idx + 1);
  });

  let source = "missing";
  if (list.some((rm) => pickBoardRawFromObject(rm))) {
    source = "rooms[].board_fields";
  } else if (
    list.some((rm) =>
      Boolean(pickBoardRawFromObject(findReservationRoomMirror(parseOrderResponseObject(order), rm)))
    )
  ) {
    source = "order_response.room_mirror";
  } else if (
    order &&
    pickFirstNonEmptyTrimmed([
      order.roombasis,
      order.roomBasis,
      order.board,
      order.boardBasis,
      order.mealPlan,
    ])
  ) {
    source = "order_root_fallback";
  } else if (missingRooms.length < list.length) {
    source = "mixed_or_packagepricedetails";
  }

  return { resolved, missingRooms, rawBoardFields, source };
}

/**
 * DEV trace for board basis hydration (hotel orders modal / PDF builders).
 *
 * @param {unknown} orderId
 * @param {Record<string, unknown>|null|undefined} order
 * @param {unknown} rooms
 */
export function traceHotelOrderBoardBasis(orderId, order, rooms) {
  let isDev = false;
  try {
    isDev = !!(typeof import.meta !== "undefined" && import.meta.env?.DEV === true);
  } catch {
    isDev = false;
  }
  if (!isDev && typeof process !== "undefined") {
    isDev = process.env.NODE_ENV === "development";
  }
  if (!isDev) return;

  const list = Array.isArray(rooms) ? rooms : [];
  const { resolved, missingRooms, rawBoardFields, source } = aggregateRoomSources(order, list);

  // eslint-disable-next-line no-console
  console.log("[HOTEL_ORDER_BOARD_BASIS_TRACE]", {
    orderId,
    source,
    roomsCount: list.length,
    resolvedBoardBasis: resolved,
    missingRooms,
    rawBoardFields,
  });
}

/**
 * Text for "פרטי הזמנה" — single line for one room, numbered lines for several.
 *
 * @param {unknown} rooms
 * @param {Record<string, unknown>|null|undefined} order
 * @returns {string}
 */
export function buildHotelOrderBoardBasisDetailText(rooms, order) {
  const list = Array.isArray(rooms) ? rooms : [];
  if (list.length === 0) return formatBoardBasisLabel(null);
  if (list.length === 1) {
    return formatBoardBasisLabel(resolveHotelRoomBoardBasis(list[0], order));
  }
  return list
    .map((room, i) => {
      const raw = resolveHotelRoomBoardBasis(room, order);
      return `חדר ${i + 1}: ${formatBoardBasisLabel(raw)}`;
    })
    .join("\n");
}
